import React, { useState, useMemo } from 'react';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  ReferenceField,
  ShowButton,
  EditButton,
  TopToolbar,
  FunctionField,
  DeleteWithConfirmButton,
  useGetOne,
} from 'react-admin';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TableSortLabel,
  Card,
  Typography,
  Box,
  TextField as MUITextField,
  FormControl,
  InputLabel,
  Select as MUISelect,
  MenuItem,
  Button,
  Grid,
  CircularProgress,
} from '@mui/material';
import { Analytics } from '@mui/icons-material';

import CustomCreateButton from '../../components/CustomCreateButton.tsx';
import RoleIconField from '@/components/RoleIconField.tsx';
import { useGetApiDashboards } from '@/api/gen/api.ts';
import { useGetList } from 'react-admin';

const GroupNameField = ({ dashboardGroupId }: { dashboardGroupId: number }) => {
  const { data: group, isLoading } = useGetOne('dashboardgroups', {
    id: dashboardGroupId,
  });
  return (
    <span>{isLoading ? 'Cargando...' : group?.groupName || 'Sin nombre'}</span>
  );
};

const PlantNameField = ({ dashboardGroupId }: { dashboardGroupId: number }) => {
  const { data: group, isLoading: loadingGroup } = useGetOne(
    'dashboardgroups',
    { id: dashboardGroupId }
  );
  const { data: plant, isLoading: loadingPlant } = useGetOne(
    'plants',
    { id: group?.plantId },
    { enabled: !!group?.plantId }
  );
  return (
    <span>
      {loadingGroup || loadingPlant
        ? 'Cargando...'
        : plant?.plantName || 'Sin planta'}
    </span>
  );
};

const FiltersBar: React.FC<{
  q: string;
  setQ: (q: string) => void;
  plantId: number | '';
  setPlantId: (plantId: number | '') => void;
  dashboardGroupId: number | '';
  setDashboardGroupId: (dashboardGroupId: number | '') => void;
  onClear: () => void;
}> = ({
  q,
  setQ,
  plantId,
  setPlantId,
  dashboardGroupId,
  setDashboardGroupId,
  onClear,
}) => {
  const { data: plants } = useGetList<any>('plants', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'plantName', order: 'ASC' },
  });
  const plantOptions: any[] = Array.isArray(plants) ? plants : [];

  const { data: groups } = useGetList<any>('dashboardgroups', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'groupName', order: 'ASC' },
    filter: plantId ? { plantId } : {},
  });
  const groupOptions: any[] = Array.isArray(groups) ? groups : [];

  React.useEffect(() => {
    setDashboardGroupId('');
  }, [plantId, setDashboardGroupId]);

  return (
    <Grid
      container
      spacing={1.5}
      alignItems='flex-start'
      sx={{
        flexShrink: 0,
        width: '100%',
      }}
    >
      {/* Campo de Búsqueda */}
      <Grid item xs={12} sm={6} md={3}>
        <MUITextField
          label='Buscar'
          placeholder='Buscar dashboards...'
          size='small'
          value={q}
          onChange={e => setQ(e.target.value)}
          sx={{ width: '100%' }}
        />
      </Grid>

      {/* Selector de Planta */}
      <Grid item xs={12} sm={6} md={3}>
        <FormControl size='small' sx={{ width: '100%' }}>
          <InputLabel id='plant-select-label'>Planta</InputLabel>
          <MUISelect
            labelId='plant-select-label'
            label='Planta'
            value={plantId}
            onChange={e => {
              const v = e.target.value as number | '';
              setPlantId(v);
            }}
            displayEmpty
            sx={{ '& .MuiSelect-select': { minWidth: 0, overflow: 'hidden' } }}
          >
            {plantOptions.map((p: any) => (
              <MenuItem key={p.id} value={p.id}>
                {p.plantName}
              </MenuItem>
            ))}
          </MUISelect>
        </FormControl>
      </Grid>

      {/* Selector de Grupo */}
      <Grid item xs={12} sm={6} md={3}>
        <FormControl size='small' sx={{ width: '100%' }} disabled={!plantId}>
          <InputLabel id='group-select-label'>Grupo</InputLabel>
          <MUISelect
            labelId='group-select-label'
            label='Grupo'
            value={dashboardGroupId}
            onChange={e => {
              const v = e.target.value as number | '';
              setDashboardGroupId(v);
            }}
            displayEmpty
            sx={{ '& .MuiSelect-select': { minWidth: 0, overflow: 'hidden' } }}
          >
            {groupOptions.map((g: any) => (
              <MenuItem key={g.id} value={g.id}>
                {g.groupName}
              </MenuItem>
            ))}
          </MUISelect>
        </FormControl>
      </Grid>

      {/* Botón Borrar Filtros  */}
      <Grid
        item
        xs={12}
        sm={6}
        md={3}
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'flex-start',
          pt: { xs: 0, sm: '8px' },
        }}
      >
        <Button
          variant='text'
          size='small'
          onClick={onClear}
          sx={{ flexShrink: 0 }}
        >
          Borrar filtros
        </Button>
      </Grid>
    </Grid>
  );
};

const DashboardListActions = ({
  q,
  setQ,
  plantId,
  setPlantId,
  dashboardGroupId,
  setDashboardGroupId,
  onClear,
}: {
  q: string;
  setQ: (q: string) => void;
  plantId: number | '';
  setPlantId: (plantId: number | '') => void;
  dashboardGroupId: number | '';
  setDashboardGroupId: (dashboardGroupId: number | '') => void;
  onClear: () => void;
}) => (
  <TopToolbar
    sx={{
      margin: 0,
      padding: 0,
      width: '100%',
      display: 'block',
    }}
  >
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'flex-end' },
        justifyContent: 'space-between',
        width: '100%',
        p: 1,
        px: 0,
        mt: 1,
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          flexShrink: 1,
          minWidth: 0,
          width: { xs: '100%', sm: 'auto' },
        }}
      >
        <FiltersBar
          q={q}
          setQ={setQ}
          plantId={plantId}
          setPlantId={setPlantId}
          dashboardGroupId={dashboardGroupId}
          setDashboardGroupId={setDashboardGroupId}
          onClear={onClear}
        />
      </Box>

      <CustomCreateButton
        label='Crear Dashboard'
        variant='contained'
        sx={{
          marginTop: { xs: 2, sm: 0 },
          alignSelf: 'flex-end',
        }}
      />
    </Box>
  </TopToolbar>
);

export const DashboardsList: React.FC = () => {
  const [q, setQ] = useState('');
  const [plantId, setPlantId] = useState<number | ''>('');
  const [dashboardGroupId, setDashboardGroupId] = useState<number | ''>('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [sort, setSort] = useState({
    field: 'name',
    order: 'ASC' as 'ASC' | 'DESC',
  });

  const { data, isLoading, error } = useGetApiDashboards({
    DashboardGroupId: dashboardGroupId ? Number(dashboardGroupId) : undefined,
    isPaginated: true,
    page,
    pageSize: perPage,
    PlantId: plantId ? Number(plantId) : undefined,
    SearchString: q || undefined,
  });

  const dashboards = (data as any)?.items || [];
  const total = (data as any)?.totalCount || 0;

  const handleClear = () => {
    setQ('');
    setPlantId('');
    setDashboardGroupId('');
    setPage(1);
  };

  if (error) {
    return (
      <Card elevation={1} sx={{ m: 2, p: 2 }}>
        <Typography color='error'>Error cargando dashboards</Typography>
      </Card>
    );
  }

  return (
    <Card elevation={1} sx={{ m: 2, p: 2 }}>
      <Box
        sx={{ p: 3, pt: 2, pb: 2.5, borderBottom: 1, borderColor: 'divider' }}
      >
        <Typography
          variant='h5'
          component='h1'
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <Analytics color='primary' />
          Gestión de Dashboards
        </Typography>
      </Box>
      <DashboardListActions
        q={q}
        setQ={setQ}
        plantId={plantId}
        setPlantId={setPlantId}
        dashboardGroupId={dashboardGroupId}
        setDashboardGroupId={setDashboardGroupId}
        onClear={handleClear}
      />
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Nombre del Dashboard</TableCell>
                <TableCell>Posición</TableCell>
                <TableCell>Grupo</TableCell>
                <TableCell>Planta</TableCell>
                <TableCell>Roles y Permisos</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dashboards.map((dashboard: any) => (
                <TableRow key={dashboard.id}>
                  <TableCell>{dashboard.id}</TableCell>
                  <TableCell>{dashboard.dashboardName}</TableCell>
                  <TableCell>{dashboard.position}</TableCell>
                  <TableCell>
                    <GroupNameField
                      dashboardGroupId={dashboard.dashboardGroupId}
                    />
                  </TableCell>
                  <TableCell>
                    <PlantNameField
                      dashboardGroupId={dashboard.dashboardGroupId}
                    />
                  </TableCell>
                  <TableCell>
                    <RoleIconField record={dashboard} />
                  </TableCell>
                  <TableCell>
                    <ShowButton record={dashboard} />
                    <EditButton record={dashboard} />
                    <DeleteWithConfirmButton
                      record={dashboard}
                      confirmTitle='Confirmar eliminación'
                      confirmContent={`¿Seguro que quieres eliminar el dashboard ? Esta acción no se puede deshacer.`}
                      mutationMode='pessimistic'
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component='div'
            count={total}
            page={page - 1}
            onPageChange={(event, newPage) => setPage(newPage + 1)}
            rowsPerPage={perPage}
            onRowsPerPageChange={event => {
              setPerPage(parseInt(event.target.value, 10));
              setPage(1);
            }}
          />
        </TableContainer>
      )}
    </Card>
  );
};

export default DashboardsList;
